import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Badge, Table, Spinner, Alert,
    ListGroup, Modal, Form, ProgressBar
} from "react-bootstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";


export default function DetailCommande() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [commande, setCommande] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showReceptionModal, setShowReceptionModal] = useState(false);
    const [lignesRecues, setLignesRecues] = useState([]);
    const [isUpdating, setIsUpdating] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);

    useEffect(() => {
        fetchCommande();
    }, [id]);

    const fetchCommande = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/commandes_fournisseurs.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setCommande(result.data);
            
            if (result.data?.lignes) {
                setLignesRecues(result.data.lignes.map(l => ({
                    id_ligne_commande: l.id_ligne_commande,
                    quantite_commandee: l.quantite_commandee,
                    quantite_recue: l.quantite_recue || 0,
                    a_recevoir: 0
                })));
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmCancel = async () => {
        setIsCancelling(true);
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/commandes_fournisseurs.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'annuler',
                    id: commande.id_commande_fournisseur
                })
            });
            
            const result = await response.json();
            
            if (result.success) {
                setShowCancelModal(false);
                fetchCommande();
            } else {
                alert(result.message || "Erreur lors de l'annulation");
                setShowCancelModal(false);
            }
        } catch (err) {
            alert("Erreur de connexion au serveur");
            setShowCancelModal(false);
        } finally {
            setIsCancelling(false);
        }
    };

    const handleReception = async () => {
        setIsUpdating(true);
        
        try {
            const token = localStorage.getItem("token");
            const lignes = lignesRecues
                .filter(l => l.a_recevoir > 0)
                .map(l => ({
                    id_ligne_commande: l.id_ligne_commande,
                    quantite_recue: l.a_recevoir
                }));
            
            const response = await fetch(`${API_BASE_URL}/commandes_fournisseurs.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'marquer_recue',
                    id: commande.id_commande_fournisseur,
                    lignes_recues: lignes
                })
            });
            
            const result = await response.json();
            
            if (result.success) {
                setShowReceptionModal(false);
                fetchCommande();
            }
        } catch (err) {
            alert("Erreur lors de la réception");
        } finally {
            setIsUpdating(false);
        }
    };

    const getStatutStyle = (statut) => {
        const styles = {
            brouillon: { color: '#64748b', icon: 'ri-draft-line', label: 'Brouillon' },
            envoyee: { color: '#3b82f6', icon: 'ri-send-plane-line', label: 'Envoyée' },
            recue_partielle: { color: '#f59e0b', icon: 'ri-stack-line', label: 'Partiellement reçue' },
            recue_complete: { color: '#10b981', icon: 'ri-check-double-line', label: 'Reçue' },
            annulee: { color: '#ef4444', icon: 'ri-close-circle-line', label: 'Annulée' }
        };
        return styles[statut] || styles.brouillon;
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement de la commande...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !commande) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Commande non trouvée</h6>
                                <p className="mb-0 small">{error || "La commande demandée n'existe pas"}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={() => navigate(-1)}>
                                    <i className="ri-arrow-left-line me-1"></i>Retour
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    const statutStyle = getStatutStyle(commande.statut);
    const totalRecu = commande.lignes?.reduce((acc, l) => acc + (l.quantite_recue || 0), 0) || 0;
    const totalCommandee = commande.lignes?.reduce((acc, l) => acc + l.quantite_commandee, 0) || 0;
    const pourcentageRecu = totalCommandee > 0 ? (totalRecu / totalCommandee) * 100 : 0;

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-shopping-cart-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">{commande.numero_commande}</h4>
                                    <p className="text-muted small mb-0">
                                        <i className="ri-store-line me-1"></i>
                                        {commande.fournisseur_nom} • {formatDate(commande.date_commande)}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate(-1)}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                            
                            {commande.statut === 'brouillon' && (
                                <Link 
                                    to={`/achats/commandesModifier/${commande.id_commande_fournisseur}`}
                                    className="rounded-pill px-3 d-flex align-items-center gap-1 btn btn-outline-primary btn-sm"
                                >
                                    <i className="ri-pencil-line"></i>
                                    Modifier
                                </Link>
                            )}
                            
                            {['envoyee', 'recue_partielle'].includes(commande.statut) && (
                                <Button 
                                    variant="success"
                                    onClick={() => setShowReceptionModal(true)}
                                    className="rounded-pill px-3 d-flex align-items-center gap-1"
                                    size="sm"
                                    style={{ background: '#10b981', border: 'none' }}
                                >
                                    <i className="ri-check-double-line"></i>
                                    Réceptionner
                                </Button>
                            )}
                            
                            {['brouillon', 'envoyee'].includes(commande.statut) && (
                                <Button 
                                    variant="outline-danger"
                                    onClick={() => setShowCancelModal(true)}
                                    className="rounded-pill px-3 d-flex align-items-center gap-1"
                                    size="sm"
                                >
                                    <i className="ri-close-circle-line"></i>
                                    Annuler
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Cartes de statistiques modernes */}
                    <Row className="g-3 mb-4">
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-primary">
                                <div className="kpi-icon">
                                    <i className="ri-information-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Statut</span>
                                    <span className="status-badge mt-1" style={{ background: `${statutStyle.color}15`, color: statutStyle.color }}>
                                        <i className={`${statutStyle.icon} me-1 fs-10`}></i>
                                        {statutStyle.label}
                                    </span>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-success">
                                <div className="kpi-icon">
                                    <i className="ri-money-euro-circle-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Montant TTC</span>
                                    <h3 className="kpi-value">{formatCurrency(commande.montant_ttc || 0)}</h3>
                                    <span className="kpi-unit">TTC</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-warning">
                                <div className="kpi-icon">
                                    <i className="ri-calendar-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Livraison prévue</span>
                                    <h3 className="kpi-value" style={{ fontSize: '1.1rem' }}>
                                        {commande.date_livraison_prevue ? formatDate(commande.date_livraison_prevue) : 'Non spécifiée'}
                                    </h3>
                                    {commande.date_livraison_prevue && (
                                        <span className={`kpi-unit ${new Date(commande.date_livraison_prevue) < new Date() ? 'text-danger' : 'text-success'}`}>
                                            {new Date(commande.date_livraison_prevue) < new Date() ? 'En retard' : 'À temps'}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-info">
                                <div className="kpi-icon">
                                    <i className="ri-stack-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Progression</span>
                                    <h3 className="kpi-value">{totalRecu}/{totalCommandee}</h3>
                                    <div className="progress-bar-custom mt-1" style={{ width: '100%' }}>
                                        <div className="progress-fill" style={{ width: `${pourcentageRecu}%`, background: '#21ACCD' }}></div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Informations détaillées */}
                    <Row className="g-4 mb-4">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0 fw-semibold">
                                        <i className="ri-store-line me-2" style={{ color: '#21ACCD' }}></i>
                                        Fournisseur
                                    </h6>
                                </Card.Header>
                                <Card.Body>
                                    <div className="info-row">
                                        <span className="info-label">Raison sociale</span>
                                        <span className="info-value fw-semibold">{commande.fournisseur_nom}</span>
                                    </div>
                                    <div className="info-row">
                                        <span className="info-label">Code</span>
                                        <span className="info-value">{commande.code_fournisseur}</span>
                                    </div>
                                    <div className="info-row">
                                        <span className="info-label">Téléphone</span>
                                        <span className="info-value">{commande.fournisseur_telephone || '-'}</span>
                                    </div>
                                    <div className="info-row">
                                        <span className="info-label">Email</span>
                                        <span className="info-value">{commande.fournisseur_email || '-'}</span>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0 fw-semibold">
                                        <i className="ri-file-list-line me-2" style={{ color: '#21ACCD' }}></i>
                                        Détails
                                    </h6>
                                </Card.Header>
                                <Card.Body>
                                    <div className="info-row">
                                        <span className="info-label">Date commande</span>
                                        <span className="info-value">{formatDate(commande.date_commande)}</span>
                                    </div>
                                    <div className="info-row">
                                        <span className="info-label">Créée par</span>
                                        <span className="info-value">{commande.prenom_utilisateur} {commande.nom_utilisateur}</span>
                                    </div>
                                    {commande.conditions_reglement && (
                                        <div className="info-row">
                                            <span className="info-label">Conditions</span>
                                            <span className="info-value">{commande.conditions_reglement}</span>
                                        </div>
                                    )}
                                    <div className="info-row">
                                        <span className="info-label">Articles</span>
                                        <span className="info-value fw-semibold">{commande.lignes?.length || 0}</span>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Tableau des produits */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Header className="bg-transparent border-0 pt-4 pb-3">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="section-icon-small">
                                        <i className="ri-stack-line text-primary"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Lignes de commande</h6>
                                </div>
                                <Badge className="px-3 py-2 rounded-pill" style={{ background: '#21ACCD' }}>
                                    {commande.lignes?.length || 0} article(s)
                                </Badge>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table className="align-middle mb-0 products-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4">Référence</th>
                                            <th>Désignation</th>
                                            <th className="text-center">Qté</th>
                                            <th className="text-end">Prix unit.</th>
                                            <th className="text-center">Remise</th>
                                            <th className="text-end">Total HT</th>
                                            <th className="text-center pe-4">Réception</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {commande.lignes?.map(ligne => (
                                            <tr key={ligne.id_ligne_commande} className="product-row">
                                                <td className="ps-4">
                                                    <span className="reference-badge">{ligne.reference}</span>
                                               </td>
                                                <td className="fw-semibold">{ligne.designation}</td>
                                                <td className="text-center">
                                                    <span className="quantity-badge">{ligne.quantite_commandee}</span>
                                             </td>
                                                <td className="text-end">{formatCurrency(ligne.prix_unitaire_ht)}</td>
                                                <td className="text-center">{ligne.remise ? `${ligne.remise}%` : '-'}</td>
                                                <td className="text-end fw-bold text-primary">{formatCurrency(ligne.montant_ligne_ht || 0)}</td>
                                                <td className="text-center pe-4">
                                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                                        <div className="progress-bar-custom" style={{ width: '60px' }}>
                                                            <div 
                                                                className="progress-fill" 
                                                                style={{ 
                                                                    width: `${(ligne.quantite_recue / ligne.quantite_commandee) * 100}%`,
                                                                    background: ligne.quantite_recue === ligne.quantite_commandee ? '#10b981' : '#f59e0b'
                                                                }}
                                                            ></div>
                                                        </div>
                                                        <Badge bg={ligne.quantite_recue === ligne.quantite_commandee ? 'success' : 'warning'} className="rounded-pill">
                                                            {ligne.quantite_recue || 0}/{ligne.quantite_commandee}
                                                        </Badge>
                                                    </div>
                                             </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Totaux */}
                    <Card className="border-0 shadow-sm rounded-3">
                        <Card.Body className="p-4">
                            <div className="recap-line d-flex justify-content-between mb-2">
                                <span className="text-muted">Total HT</span>
                                <span className="fw-bold">{formatCurrency(commande.montant_ht || 0)}</span>
                            </div>
                            <div className="recap-line d-flex justify-content-between mb-2">
                                <span className="text-muted">TVA (18%)</span>
                                <span className="text-warning fw-semibold">{formatCurrency(commande.montant_tva || 0)}</span>
                            </div>
                            <div className="recap-line d-flex justify-content-between mb-3">
                                <span className="text-muted">CSS (1%)</span>
                                <span className="text-info fw-semibold">{formatCurrency(commande.montant_css || 0)}</span>
                            </div>
                            <hr />
                            <div className="recap-line d-flex justify-content-between">
                                <span className="fw-bold fs-5">Total TTC</span>
                                <span className="fw-bold text-primary fs-4">{formatCurrency(commande.montant_ttc || 0)}</span>
                            </div>
                        </Card.Body>
                    </Card>

                    <Footer />
                </div>
            </div>

            {/* Modal de réception modernisé */}
            <Modal show={showReceptionModal} onHide={() => setShowReceptionModal(false)} size="lg" centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#21ACCD' }}>
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="confirm-icon mb-3">
                                <i className="ri-inbox-archive-line text-white" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold text-white">Réceptionner la commande</h3>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-4">
                    <div className="table-responsive">
                        <Table className="align-middle reception-table">
                            <thead className="bg-light">
                                <tr>
                                    <th>Produit</th>
                                    <th className="text-center">Commandé</th>
                                    <th className="text-center">Reçu</th>
                                    <th className="text-center">Restant</th>
                                    <th className="text-center">À recevoir</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lignesRecues.map((ligne, index) => {
                                    const restant = ligne.quantite_commandee - ligne.quantite_recue;
                                    const produit = commande.lignes[index];
                                    return (
                                        <tr key={ligne.id_ligne_commande}>
                                            <td>
                                                <div className="fw-semibold">{produit?.designation}</div>
                                                <small className="text-muted">{produit?.reference}</small>
                                            </td>
                                            <td className="text-center">
                                                <span className="quantity-badge">{ligne.quantite_commandee}</span>
                                            </td>
                                            <td className="text-center">
                                                <Badge bg="success" className="rounded-pill px-3">{ligne.quantite_recue}</Badge>
                                            </td>
                                            <td className="text-center">
                                                <Badge bg="warning" className="rounded-pill px-3">{restant}</Badge>
                                            </td>
                                            <td className="text-center">
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    max={restant}
                                                    value={ligne.a_recevoir}
                                                    onChange={(e) => {
                                                        const newLignes = [...lignesRecues];
                                                        newLignes[index].a_recevoir = parseInt(e.target.value) || 0;
                                                        setLignesRecues(newLignes);
                                                    }}
                                                    size="sm"
                                                    style={{ width: '80px', margin: '0 auto' }}
                                                    className="text-center border-2 rounded-3"
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                    <Button variant="outline-secondary" onClick={() => setShowReceptionModal(false)} className="rounded-pill px-4">
                        Annuler
                    </Button>
                    <Button 
                        variant="success" 
                        onClick={handleReception}
                        disabled={isUpdating || lignesRecues.every(l => l.a_recevoir === 0)}
                        className="rounded-pill px-5"
                        style={{ background: '#10b981', border: 'none' }}
                    >
                        {isUpdating ? <Spinner size="sm" className="me-2" /> : <i className="ri-check-double-line me-2"></i>}
                        Confirmer la réception
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal d'annulation modernisé */}
            <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#ef4444' }}>
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="confirm-icon mb-3">
                                <i className="ri-alert-line text-white" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold text-white">Annuler la commande</h3>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-4 text-center">
                    <p className="text-muted mb-4">
                        Êtes-vous sûr de vouloir annuler la commande <strong className="text-primary">{commande.numero_commande}</strong> ?
                    </p>
                    <Alert variant="warning" className="rounded-3">
                        <i className="ri-information-line me-2"></i>
                        Cette action est irréversible.
                    </Alert>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                    <Button variant="outline-secondary" onClick={() => setShowCancelModal(false)} className="rounded-pill px-4">
                        Annuler
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={handleConfirmCancel}
                        disabled={isCancelling}
                        className="rounded-pill px-5"
                    >
                        {isCancelling ? <Spinner size="sm" className="me-2" /> : <i className="ri-check-line me-2"></i>}
                        Confirmer
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: #21ACCD; }
                .kpi-success { border-left-color: #10b981; }
                .kpi-warning { border-left-color: #f59e0b; }
                .kpi-info { border-left-color: #0ea5e9; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-info .kpi-icon { background: #e2f3f5; color: #0ea5e9; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.8rem;
                    border-radius: 30px;
                    font-size: 0.7rem;
                    font-weight: 500;
                }
                
                /* Info Rows */
                .info-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.75rem 0;
                    border-bottom: 1px dashed #e2e8f0;
                }
                .info-row:last-child {
                    border-bottom: none;
                }
                .info-label {
                    color: #64748b;
                    font-size: 0.85rem;
                }
                .info-value {
                    font-weight: 500;
                    color: #1e293b;
                }
                
                /* Reference Badge */
                .reference-badge {
                    background: #f1f5f9;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-family: monospace;
                }
                
                .quantity-badge {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 50px;
                    padding: 0.25rem 0.5rem;
                    background: #f1f5f9;
                    border-radius: 12px;
                    font-weight: 600;
                    font-size: 0.85rem;
                    color: #1e293b;
                }
                
                /* Products Table */
                .products-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                .products-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                .product-row {
                    transition: all 0.2s ease;
                }
                .product-row:hover {
                    background: #f8fafc;
                }
                
                /* Progress Bar */
                .progress-bar-custom {
                    background: #e2e8f0;
                    border-radius: 4px;
                    height: 6px;
                    overflow: hidden;
                }
                .progress-fill {
                    height: 100%;
                    border-radius: 4px;
                    transition: width 0.3s ease;
                }
                
                /* Recap Line */
                .recap-line {
                    padding: 0.5rem 0;
                }
                
                /* Section Icon Small */
                .section-icon-small {
                    width: 32px;
                    height: 32px;
                    background: linear-gradient(135deg, #21ACCD20, #1a8ba820);
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                /* Modern Modal */
                .modern-modal .modal-content {
                    border-radius: 20px;
                    border: none;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                    overflow: hidden;
                }
                .confirm-icon {
                    animation: scaleIn 0.5s ease-out;
                }
                @keyframes scaleIn {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); opacity: 1; }
                }
                
                /* Reception Table */
                .reception-table thead th {
                    background: #f8fafc;
                    padding: 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                    text-transform: uppercase;
                }
                .reception-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                }
                
                /* Utilitaires */
                .rounded-3 {
                    border-radius: 12px !important;
                }
                .rounded-pill {
                    border-radius: 50px !important;
                }
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
            `}</style>
        </>
    );
}